// src/pages/SustainabilityPage.jsx
import React, { useEffect, useRef } from 'react';
import { Leaf, Recycle, Droplets, Trees, Cloud, Zap, Target, BarChart } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Breadcrumbs from '../components/Shared/Breadcrumbs';
import { Link } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const SustainabilityPage = () => {
  const pageRef = useRef(null);

  const metrics = [
    { icon: Droplets, label: 'Water Savings', value: '90%', description: 'Reduced water usage vs traditional protein' },
    { icon: Trees, label: 'Land Efficiency', value: '95%', description: 'Less land required per kg protein' },
    { icon: Cloud, label: 'Carbon Footprint', value: '80%', description: 'Lower greenhouse gas emissions' },
    { icon: Zap, label: 'Feed Conversion', value: '2.2:1', description: 'Superior feed-to-protein ratio' },
  ];

  const circularEconomySteps = [
    {
      title: 'Optimized Inputs',
      description: 'Precision nutrient formulation minimizing resource waste',
      icon: '♻️'
    },
    {
      title: 'Maximized Outputs',
      description: 'Every resource converted to valuable protein product',
      icon: '⚡'
    },
    {
      title: 'By-product Utilization',
      description: 'Residual materials repurposed for agriculture',
      icon: '🌱'
    },
    {
      title: 'Waste Minimization',
      description: 'Closed-loop systems with near-zero waste',
      icon: '📉'
    }
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Metrics counter animation
      metrics.forEach((metric, index) => {
        gsap.fromTo(`.metric-value-${index}`, 
          { innerText: 0 },
          {
            innerText: metric.value.replace('%', '').replace(':', ''),
            duration: 2,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: `.metric-${index}`,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
            onUpdate: function() {
              const el = document.querySelector(`.metric-value-${index}`);
              if (el) {
                const value = Math.floor(this.targets()[0].innerText);
                el.innerText = metric.value.includes('%') ? `${value}%` : 
                              metric.value.includes(':') ? `${value}:1` : value;
              }
            }
          }
        );
      });

      // Circular economy animation
      gsap.fromTo('.circular-step', {
        scale: 0.8,
        opacity: 0,
        rotation: -180,
      }, {
        scale: 1,
        opacity: 1,
        rotation: 0,
        duration: 1,
        stagger: 0.3,
        ease: 'back.out(1.7)',
        scrollTrigger: {
          trigger: '.circular-section',
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      });

      // Timeline for hero section
      const tl = gsap.timeline();
      tl.from('.sustainability-title', {
        y: 100,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
      })
      .from('.sustainability-subtitle', {
        y: 50,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.5')
      .from('.leaf-icon', {
        scale: 0,
        rotation: 360,
        duration: 1,
        ease: 'back.out(1.7)',
      }, '-=0.3');
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef} className="pt-20">
      <Breadcrumbs currentPage="Sustainability" />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/20 via-dark-800 to-emerald-900/20" />
        <div className="absolute inset-0 glitter-bg" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="leaf-icon inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 mb-8">
              <Leaf className="w-12 h-12 text-white" />
            </div>
            
            <h1 className="sustainability-title text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-green-400 to-emerald-600 bg-clip-text text-transparent">
                Sustainability Is Not an Initiative
              </span>
              <br />
              <span className="text-white">It Is the System</span>
            </h1>
            
            <p className="sustainability-subtitle text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
              Our entire cultivation and manufacturing model is engineered to operate within environmental limits, while delivering industrial-scale output.
            </p>
            
            <div className="w-32 h-1 bg-gradient-to-r from-green-400 to-emerald-600 mx-auto" />
          </div>
        </div>
      </section>

      {/* Environmental Benefits */}
      <section className="py-20 bg-dark-900">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 gold-gradient">
              Environmental Benefits by Design
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-20">
              <div>
                <h3 className="text-2xl font-bold text-white mb-6">
                  Traditional vs. Our System
                </h3>
                <div className="space-y-6">
                  {[
                    {
                      label: 'Water Consumption',
                      traditional: 'High',
                      ours: 'Minimal',
                      color: 'from-blue-500 to-cyan-500'
                    },
                    {
                      label: 'Land Footprint',
                      traditional: 'Extensive',
                      ours: 'Compact',
                      color: 'from-green-500 to-emerald-500'
                    },
                    {
                      label: 'Climate Dependency',
                      traditional: 'High',
                      ours: 'Independent',
                      color: 'from-yellow-500 to-amber-500'
                    },
                    {
                      label: 'Resource Efficiency',
                      traditional: 'Low',
                      ours: 'High',
                      color: 'from-purple-500 to-pink-500'
                    }
                  ].map((item, index) => (
                    <div key={index} className="bg-dark-800/50 rounded-xl p-6 border border-gold-500/20">
                      <h4 className="text-lg font-semibold text-white mb-4">{item.label}</h4>
                      <div className="flex justify-between items-center">
                        <div className="text-center">
                          <div className={`w-24 h-2 bg-gradient-to-r ${item.color} rounded-full opacity-50`} />
                          <p className="text-gray-400 text-sm mt-2">Traditional</p>
                          <p className="text-gray-300 font-semibold">{item.traditional}</p>
                        </div>
                        <div className="text-gold-400">
                          →
                        </div>
                        <div className="text-center">
                          <div className={`w-24 h-2 bg-gradient-to-r ${item.color} rounded-full`} />
                          <p className="text-gray-400 text-sm mt-2">Our System</p>
                          <p className="text-green-400 font-semibold">{item.ours}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="text-center">
                <div className="relative inline-block">
                  <div className="w-64 h-64 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-600/10 border border-green-500/30 flex items-center justify-center">
                    <Recycle className="w-32 h-32 text-green-400 animate-spin-slow" />
                  </div>
                  <div className="absolute -inset-4 border border-green-500/20 rounded-full animate-pulse"></div>
                </div>
                <p className="text-gray-300 mt-8 max-w-md mx-auto">
                  This allows us to produce protein without amplifying ecological pressure, creating a truly sustainable protein source.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-20 bg-dark-800/30">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 gold-gradient">
            Quantifiable Impact
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {metrics.map((metric, index) => (
              <div key={index} className={`metric-${index} relative group`}>
                <div className="bg-dark-900/50 rounded-xl p-8 border border-gold-500/20 text-center hover:border-gold-500/40 transition-all duration-300 hover:shadow-2xl hover:shadow-gold-500/10">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 mx-auto mb-6 flex items-center justify-center">
                    <metric.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className={`metric-value-${index} text-4xl font-bold gold-gradient mb-2`}>
                    {metric.value}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{metric.label}</h3>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Circular Economy */}
      <section className="circular-section py-20 bg-dark-900">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 gold-gradient">
              Circular Economy at Our Core
            </h2>
            
            <div className="relative mb-16">
              {/* Circular Diagram */}
              <div className="relative w-64 h-64 md:w-80 md:h-80 mx-auto">
                {/* Outer Circle */}
                <div className="absolute inset-0 rounded-full border-4 border-green-500/30 animate-spin-slow"></div>
                
                {/* Inner Circles */}
                <div className="absolute inset-12 rounded-full border-2 border-emerald-500/50 animate-spin-slow-reverse"></div>
                <div className="absolute inset-24 rounded-full border border-gold-500/30 animate-pulse"></div>
                
                {/* Center */}
                <div className="absolute inset-32 rounded-full bg-gradient-to-br from-green-600 to-emerald-700 flex items-center justify-center">
                  <Recycle className="w-12 h-12 text-white" />
                </div>
                
                {/* Steps around circle */}
                {circularEconomySteps.map((step, index) => {
                  const angle = (index * 90) * (Math.PI / 180);
                  const radius = 140;
                  const x = radius * Math.cos(angle);
                  const y = radius * Math.sin(angle);
                  
                  return (
                    <div
                      key={index}
                      className="circular-step absolute"
                      style={{
                        left: `calc(50% + ${x}px)`,
                        top: `calc(50% + ${y}px)`,
                        transform: 'translate(-50%, -50%)',
                      }}
                    >
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-dark-800 to-dark-900 border-2 border-gold-500/30 flex flex-col items-center justify-center p-4 transform -translate-x-1/2 -translate-y-1/2 hover:scale-110 transition-transform duration-300">
                        <span className="text-2xl mb-1">{step.icon}</span>
                        <span className="text-xs text-gray-300 text-center">{step.title}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {circularEconomySteps.map((step, index) => (
                <div key={index} className="bg-dark-800/50 rounded-xl p-6 border border-gold-500/20">
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl">{step.icon}</div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                      <p className="text-gray-300">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-2xl p-8 border border-green-500/20">
              <p className="text-xl text-center text-gray-300 italic">
                "This circular approach ensures that every resource contributes to value creation, not disposal. 
                Sustainability is embedded in our operational logic, not added later."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Long-term Goals */}
<section className="py-20 bg-dark-800/30 overflow-hidden">
  <div className="container mx-auto px-4 sm:px-6">
    <div className="max-w-6xl mx-auto">
      
      {/* Section Header */}
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 gold-gradient">
          Our Sustainability Roadmap
        </h2>
        <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
          Our sustainability journey is structured as a phased, research-to-infrastructure roadmap, 
          ensuring that growth is responsible, compliant, and scalable.
        </p>
      </div>

      {/* Timeline Container */}
      <div className="relative">
        
        {/* Animated Timeline Line */}
        <div className="hidden md:block absolute left-0 right-0 top-1/2 transform -translate-y-1/2 h-1 bg-gradient-to-r from-green-500 via-emerald-600 to-green-500 rounded-full overflow-hidden">
          <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        </div>
        
        {/* Mobile Timeline - Vertical */}
        <div className="md:hidden absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-green-500 via-emerald-600 to-green-500 rounded-full">
          <div className="absolute inset-0 animate-shimmer bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
        </div>
        
        {/* Phases Grid */}
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {[
            {
              year: '2024',
              title: 'Research & Validation Phase',
              description: 'Focused on building a strong foundation through scientific and strategic clarity.',
              goals: [
                'Research studies on sustainable protein systems',
                'Evaluation of mealworm-based protein potential',
                'Process understanding and feasibility analysis',
                'Alignment with global sustainability and food system trends'
              ],
              color: 'from-green-500 to-emerald-600',
              phaseNumber: '01'
            },
            {
              year: '2025',
              title: 'Processing & Project Implementation',
              description: 'Focused on execution and translating research into practical, measurable operations.',
              goals: [
                'Establishment of processing workflows',
                'Development of operational and quality systems',
                'Implementation of sustainability-aligned practices',
                'Preparation for compliance and regulatory readiness'
              ],
              color: 'from-emerald-500 to-teal-600',
              phaseNumber: '02'
            },
            {
              year: '2026',
              title: 'Establishment & Scale-Up Planning',
              description: 'Focused on long-term impact and creating a durable, future-ready protein platform.',
              goals: [
                'Planning for full-scale establishment',
                'Expansion of sustainable infrastructure',
                'Integration of circular economy principles',
                'Preparation for broader market and export alignment'
              ],
              color: 'from-teal-500 to-cyan-600',
              phaseNumber: '03'
            }
          ].map((phase, index) => (
            <div key={index} className="relative">
              
              {/* Timeline Node - Animated */}
              <div className="absolute md:top-1/2 md:left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 
                            left-0 top-0 transform -translate-x-1/2 md:translate-x-0">
                <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full bg-gradient-to-br ${phase.color} animate-pulse`}>
                  <div className="absolute -inset-2 border-2 border-green-500/30 rounded-full animate-ping"></div>
                </div>
                <div className="md:hidden absolute -top-6 left-1/2 transform -translate-x-1/2">
                  <span className="text-sm font-bold text-green-400">{phase.year}</span>
                </div>
              </div>
              
              {/* Phase Card */}
              <div className="phase-card ml-10 md:ml-0 mt-4 md:mt-0 md:transform md:transition-all md:duration-300 
                            md:hover:scale-[1.02] md:hover:-translate-y-2">
                <div className="bg-dark-900/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-gold-500/20 
                              hover:border-green-500/40 transition-all duration-500 shadow-xl hover:shadow-2xl 
                              hover:shadow-green-500/10 overflow-hidden group">
                  
                  {/* Phase Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="text-xs md:text-sm text-green-400 font-semibold uppercase tracking-widest mb-1">
                        Phase {phase.phaseNumber}
                      </div>
                      <div className="text-2xl md:text-3xl font-bold text-white mb-2">{phase.year}</div>
                    </div>
                    <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br ${phase.color} 
                                  flex items-center justify-center text-dark-900 font-bold text-lg md:text-xl 
                                  transform group-hover:scale-110 transition-transform duration-300`}>
                      {phase.phaseNumber}
                    </div>
                  </div>
                  
                  {/* Phase Title */}
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-4 leading-tight">
                    {phase.title}
                  </h3>
                  
                  {/* Phase Description */}
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {phase.description}
                  </p>
                  
                  {/* Goals List */}
                  <div className="space-y-3">
                    <div className="flex items-start">
                      {/* <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mr-3 mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-green-400"></div>
                      </div> */}
                      {/* <span className="text-gray-300 text-sm md:text-base">
                        Focused on {phase.title.toLowerCase().includes('research') ? 'building a strong foundation' : 
                        phase.title.toLowerCase().includes('processing') ? 'execution and system development' : 
                        'long-term impact and growth'}
                      </span> */}
                    </div>
                    
                    {phase.goals.map((goal, goalIndex) => (
                      <div 
                        key={goalIndex} 
                        className="flex items-start opacity-0 animate-fade-in-up"
                        style={{ animationDelay: `${goalIndex * 0.1}s` }}
                      >
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center mr-3 mt-0.5">
                          <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-gray-300 text-sm md:text-base">{goal}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Progress Bar - Animated */}
                  <div className="mt-8 pt-6 border-t border-green-500/20">
                    <div className="flex justify-between text-xs text-gray-400 mb-2">
                      <span>Progress</span>
                      <span>{index === 0 ? '50%' : index === 1 ? 'Coming Soon' : 'Future'}</span>
                    </div>
                    <div className="h-2 bg-dark-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full bg-gradient-to-r ${phase.color} transition-all duration-1000 ease-out`}
                        style={{ 
                          width: index === 0 ? '50%' : '0%',
                          animation: index === 0 ? 'pulse-width 2s infinite' : 'none'
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Long-Term Commitment Section */}
      <div className="mt-20 md:mt-24 bg-gradient-to-r from-green-900/20 via-emerald-900/10 to-green-900/20 
                    rounded-2xl p-6 md:p-10 border border-green-500/20 backdrop-blur-sm">
        
        <div className="text-center mb-8">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Our Long-Term Commitment
          </h3>
          <p className="text-gray-300 max-w-3xl mx-auto">
            Each phase of our roadmap is guided by deliberate planning and execution — not shortcuts.
          </p>
        </div>
        
        {/* Commitment Points */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[
            {
              title: 'Resource Efficiency',
              description: 'Maximizing output while minimizing input',
              icon: '🌱'
            },
            {
              title: 'Environmental Responsibility',
              description: 'Reducing ecological footprint at every step',
              icon: '🌍'
            },
            {
              title: 'Process Discipline',
              description: 'Consistent, measurable, and scalable operations',
              icon: '⚙️'
            },
            {
              title: 'Long-term Value Creation',
              description: 'Building sustainable impact for future generations',
              icon: '📈'
            }
          ].map((commitment, index) => (
            <div 
              key={index} 
              className="commitment-card bg-dark-900/40 rounded-xl p-5 border border-gold-500/10 
                        hover:border-green-500/30 transition-all duration-300 group hover:scale-[1.02]"
            >
              <div className="text-3xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                {commitment.icon}
              </div>
              <h4 className="text-lg font-bold text-white mb-2">{commitment.title}</h4>
              <p className="text-gray-400 text-sm">{commitment.description}</p>
              <div className="mt-4 h-0.5 w-0 group-hover:w-full bg-gradient-to-r from-green-500 to-emerald-600 
                            transition-all duration-500"></div>
            </div>
          ))}
        </div>
        
        {/* Final Statement */}
        <div className="mt-10 text-center">
          <p className="text-lg md:text-xl text-gray-300 italic max-w-3xl mx-auto">
            "We believe sustainability is achieved step by step, through deliberate planning and execution — not shortcuts."
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <div className="px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full text-green-400 text-sm">
              Phase-Driven Approach
            </div>
            <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm">
              Research to Infrastructure
            </div>
            <div className="px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full text-teal-400 text-sm">
              Responsible Growth
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-green-900/20 via-dark-800 to-emerald-900/20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 gold-gradient">
            Partner in Sustainable Innovation
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Join us in building a sustainable protein future. Together, we can create meaningful environmental impact.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="mailto:info@profitmix.in?subject=Sustainability Partnership"
              className="px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-700 text-white font-bold text-lg rounded-full hover:from-green-500 hover:to-emerald-600 transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-green-500/30"
            >
              Explore Sustainable Partnerships
            </a>
            <Link
              to="/certifications"
              className="px-8 py-4 border-2 border-gold-500 text-gold-300 font-bold text-lg rounded-full hover:bg-gold-500/10 transition-all duration-300 transform hover:scale-105"
            >
              View Our Certifications
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SustainabilityPage;
